import { Asserts } from 'yup';
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import schema from './schema';
import { addTemplate } from '../../../services/templates';
import { Template } from '../../../@types/domain';
import { logEvent, TrackingEvents } from '../../../tracking';

type AddTemplateInputs = Asserts<typeof schema>;

function useAddTemplateMutation(
  mutationOptions: UseMutationOptions<
    Template | undefined,
    Error,
    AddTemplateInputs
  >
) {
  return useMutation<Template | undefined, Error, AddTemplateInputs>(
    ['templates', 'addTemplate'],
    (inputs) => addTemplate(inputs.name),
    mutationOptions
  );
}

function useAddTemplateForm(
  templates: Template[],
  mutationOptions: UseMutationOptions<
    Template | undefined,
    Error,
    AddTemplateInputs
  >
) {
  const mutation = useAddTemplateMutation(mutationOptions);
  const queryClient = useQueryClient();

  const form = useForm<AddTemplateInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    context: {
      templates,
      ok: true,
    },
  });

  const {
    mutateAsync: saveTemplate,
    error,
    isLoading: isSavingTemplate,
    reset: resetMutation,
  } = mutation;

  const {
    handleSubmit,
    reset: resetForm,
    formState: { isSubmitting },
  } = form;

  const isLoading = isSavingTemplate || isSubmitting;

  const onSubmit: SubmitHandler<AddTemplateInputs> = async (data) => {
    const result = await saveTemplate(data);
    logEvent(TrackingEvents.CREATE_TEMPLATE);
    resetMutation();
    resetForm();
    await queryClient.invalidateQueries(['templates']);
    return result;
  };

  return { ...form, error, onSubmit: handleSubmit(onSubmit), isLoading };
}

export default useAddTemplateForm;
